import React, { useEffect, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { useParams } from 'react-router';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { DealerPortalSectionEnumValue, getDealerPortalSectionValue } from 'enums/DealerPortalSectionEnum';
import { useDealerPortalMenuTree } from 'services/Data/DealerPortalMenuServices';
import InventoryItemCategory from '../InventoryItemCategories/InventoryItemCategory';
import Tooling from '../Tooling/Tooling';
import Wall from '../Wall/Wall';
import MerchandisingProgramQuoteLandingInfo from '../MerchandisingProgramQuote/MerchandisingProgramQuoteLandingInfo';
import ExteriorSignageQuote from '../ExteriorSignageQuote/ExteriorSignageQuote';

const getNodeId = node => node?.id;
const getNodeChildren = node => (Array.isArray(node?.children) ? node.children : []);
const getNodeMenuProducts = node => (Array.isArray(node?.menuProducts) ? node.menuProducts : []);
const normalizeProductType = productType => (productType || '').toLowerCase();

const findMenuInTree = (treeNodes, menuId) => {
  const stack = [...(Array.isArray(treeNodes) ? treeNodes : [])];

  while (stack.length > 0) {
    const node = stack.pop();
    if (!node) {
      continue;
    }

    if (getNodeId(node) === menuId) {
      return node;
    }

    const children = getNodeChildren(node);
    if (children.length > 0) {
      stack.push(...children);
    }
  }

  return null;
};

const DealerPortalMenuProductDetail = () => {
  const { t } = useTranslation();
  const { sectionName, dealerPortalMenuId, dealerPortalMenuProductId } = useParams();
  const accountReducer = useSelector(state => state.AccountReducer);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo(0, 0);
    }
  }, [sectionName, dealerPortalMenuId, dealerPortalMenuProductId]);

  const parsedSectionValue = getDealerPortalSectionValue(sectionName);
  const section =
    parsedSectionValue > DealerPortalSectionEnumValue.None
      ? parsedSectionValue
      : DealerPortalSectionEnumValue.DisplaysToolingAndFurniture;

  const { data: menuTreeData, isLoading } = useDealerPortalMenuTree(section, accountReducer.isLoggedIn);

  const selectedMenu = useMemo(() => findMenuInTree(menuTreeData, dealerPortalMenuId), [menuTreeData, dealerPortalMenuId]);

  const normalizedProductType = useMemo(() => {
    const selectedMenuProduct = getNodeMenuProducts(selectedMenu).find(
      product => product?.productId === dealerPortalMenuProductId,
    );

    return normalizeProductType(selectedMenuProduct?.productType) || null;
  }, [selectedMenu, dealerPortalMenuProductId]);

  if (isLoading) {
    return (
      <Box p={3}>
        <Typography>{t('loading')}</Typography>
      </Box>
    );
  }

  if (normalizedProductType === 'inventoryitem') {
    return <InventoryItemCategory />;
  }

  if (normalizedProductType === 'tooling') {
    return <Tooling />;
  }

  if (normalizedProductType === 'paneltype') {
    return <Wall />;
  }

  if (normalizedProductType === 'exteriorsignage') {
    return <ExteriorSignageQuote />;
  }

  if (normalizedProductType === 'merchandisingprogram') {
    return <MerchandisingProgramQuoteLandingInfo />;
  }

  return (
    <Box p={3}>
      <Typography variant="body1" color="textSecondary">
        {t('noItemsToDisplay')}
      </Typography>
    </Box>
  );
};

export default DealerPortalMenuProductDetail;
